/**
 * @file Courses hooks — list + install/update/uninstall mutations,
 *       all backed by TanStack so the catalog panel stays consistent
 *       with Learners / Settings.
 * @module @labcraft/admin/ui/hooks/use-courses
 *
 * Every mutation invalidates the `['admin','courses']` prefix on
 * success so the list re-fetches with the new row state. Install,
 * update, and uninstall each also call `refresh()` to nudge lab's
 * registry — that nudge goes away in Part 4 of #693 when admin
 * writes the table directly and lab learns of changes via
 * `lc_courses` LISTEN/NOTIFY.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * / `useMutation` outside it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type {
  InstallCourseRequest,
  InstalledCourse,
  UpdateInstalledCourseRequest,
} from '../../shared/courses_routes.ts'
import type { Admin } from '../../client/index.ts'
import { qkCoursesList } from './query_keys.ts'

/** Subscribe to the catalog of installed courses. */
export function useCoursesList() {
  const client = useClient<Admin>()
  return useQuery<InstalledCourse[]>({
    queryKey: qkCoursesList,
    queryFn: () => client.courses.list(),
  })
}

/**
 * Install a course row. Follows up with `refresh()` so lab re-reads
 * its registry before invalidating — keeps the post-install render
 * coherent with lab's state. Both calls run inside the mutation so
 * the panel sees a single `isPending` window.
 */
export function useInstallCourseMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation<InstalledCourse, Error, InstallCourseRequest>({
    mutationFn: async args => {
      const row = await client.courses.install(args)
      await client.courses.refresh()
      return row
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] })
    },
  })
}

/**
 * Patch a course row (today: flip the `enabled` flag). Same
 * refresh-then-invalidate flow as install.
 */
export function useUpdateCourseMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation<
    InstalledCourse,
    Error,
    { courseId: string; patch: UpdateInstalledCourseRequest }
  >({
    mutationFn: async ({ courseId, patch }) => {
      const row = await client.courses.update(courseId, patch)
      await client.courses.refresh()
      return row
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] })
    },
  })
}

/**
 * Remove a course row. Same refresh-then-invalidate flow as install.
 */
export function useUninstallCourseMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation<{ deleted: true }, Error, string>({
    mutationFn: async courseId => {
      const result = await client.courses.uninstall(courseId)
      await client.courses.refresh()
      return result
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] })
    },
  })
}
